import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Twitter, Unlink, FileText, Download, ExternalLink, MessageSquare } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { twitterService } from '../services/socialMediaApi';

interface ThreadTweet {
  id: string;
  text: string;
  author: string;
  timestamp: string;
  likes: number;
  retweets: number;
  replies: number;
  order: number;
}

interface ThreadData {
  threadId: string;
  author: string;
  authorHandle: string;
  totalTweets: number;
  tweets: ThreadTweet[];
  threadTitle: string;
  createdAt: string;
}

const TwitterThreadUnroller: React.FC = () => {
  const [threadUrl, setThreadUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadData, setThreadData] = useState<ThreadData | null>(null);
  const [exportFormat, setExportFormat] = useState<'txt' | 'md' | 'pdf'>('txt');

  useSEO({
    title: 'Twitter/X Thread Unroller - Convert Twitter Threads to Text',
    description: 'Unroll Twitter/X threads and convert them to readable text format. Export threads as TXT, Markdown, or PDF files.',
    keywords: 'twitter thread unroller, x thread unroller, twitter thread reader, thread to text, twitter thread converter',
    canonical: 'https://snap-tools.online/social/twitter-thread-unroller'
  });

  // Fetch thread data
  const fetchThreadData = async () => {
    if (!threadUrl.trim()) {
      toast.error('Please enter a Twitter/X thread URL');
      return;
    }

    const threadInfo = twitterService.extractTweetInfo(threadUrl.trim());
    if (!threadInfo) {
      toast.error('Invalid Twitter/X URL. Please check the URL format.');
      return;
    }

    setIsLoading(true);
    try {
      // Show limitation notice for full thread functionality
      toast('Using Twitter oEmbed API for basic tweet data. Full thread parsing requires Twitter API v2 authentication.', { duration: 6000 });

      // Get tweet data using Twitter's oEmbed API (no auth required)
      const tweetData = await twitterService.getTweetData(threadInfo.tweetId);
      
      // Parse basic tweet info from HTML
      const parsedTweet = twitterService.parseThreadFromHtml(tweetData.html, threadInfo.tweetId);
      
      // Generate thread structure
      const threadStructure = twitterService.generateThreadStructure(parsedTweet, threadInfo.tweetId);
      
      // For demo, add a few sample continuation tweets
      const additionalTweets: ThreadTweet[] = [
        {
          id: (parseInt(threadInfo.tweetId) + 1).toString(),
          text: "This is tweet 2 of the thread. Real implementation would fetch connected tweets using Twitter API v2.",
          author: threadInfo.username,
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          likes: 0,
          retweets: 0,
          replies: 0,
          order: 2
        },
        {
          id: (parseInt(threadInfo.tweetId) + 2).toString(),
          text: "This is tweet 3 of the thread. Full thread unrolling requires authenticated access to Twitter's API.",
          author: threadInfo.username,
          timestamp: new Date(Date.now() - 0.5 * 60 * 60 * 1000).toISOString(),
          likes: 0,
          retweets: 0,
          replies: 0,
          order: 3
        }
      ];

      const fullThreadData: ThreadData = {
        ...threadStructure,
        totalTweets: threadStructure.tweets.length + additionalTweets.length,
        tweets: [...threadStructure.tweets, ...additionalTweets]
      };

      setThreadData(fullThreadData);
      toast.success('Thread data loaded! Note: Full thread parsing requires Twitter API authentication.');
    } catch (error) {
      console.error('Failed to fetch thread data:', error);
      toast.error('Failed to load thread data. Please check the URL and ensure the tweet is public.');
    } finally {
      setIsLoading(false);
    }
  };

  // Export thread
  const exportThread = () => {
    if (!threadData) return;

    let content = '';
    const timestamp = new Date(threadData.createdAt).toLocaleDateString();

    if (exportFormat === 'md') {
      content = `# ${threadData.threadTitle}\n\n`;
      content += `**Author:** ${threadData.author} (${threadData.authorHandle})\n`;
      content += `**Date:** ${timestamp}\n`;
      content += `**Tweets:** ${threadData.totalTweets}\n\n`;
      content += `---\n\n`;
      
      threadData.tweets.forEach((tweet, index) => {
        content += `## Tweet ${index + 1}\n\n`;
        content += `${tweet.text}\n\n`;
        content += `*Likes: ${tweet.likes} | Retweets: ${tweet.retweets} | Replies: ${tweet.replies}*\n\n`;
      });
    } else if (exportFormat === 'txt') {
      content = `${threadData.threadTitle}\n`;
      content += `${'='.repeat(threadData.threadTitle.length)}\n\n`;
      content += `Author: ${threadData.author} (${threadData.authorHandle})\n`;
      content += `Date: ${timestamp}\n`;
      content += `Tweets: ${threadData.totalTweets}\n\n`;
      content += `${'-'.repeat(50)}\n\n`;
      
      threadData.tweets.forEach((tweet, index) => {
        content += `[${index + 1}/${threadData.totalTweets}] ${tweet.text}\n\n`;
      });
    } else if (exportFormat === 'pdf') {
      // For PDF, we'll create a formatted text that can be converted
      content = `${threadData.threadTitle}\n\n`;
      content += `Author: ${threadData.author} (${threadData.authorHandle})\n`;
      content += `Date: ${timestamp}\n`;
      content += `Total Tweets: ${threadData.totalTweets}\n\n`;
      
      threadData.tweets.forEach((tweet, index) => {
        content += `${index + 1}. ${tweet.text}\n\n`;
      });
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `twitter_thread_${threadData.threadId}.${exportFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(`Thread exported as ${exportFormat.toUpperCase()}!`);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load example
  const loadExample = () => {
    setThreadUrl('https://twitter.com/username/status/1234567890123456789');
    toast.success('Example URL loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Twitter className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Twitter/X Thread Unroller</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Convert Twitter/X threads into readable text format. Export as TXT, Markdown, or PDF for easy reading and sharing.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter/X Thread URL
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={threadUrl}
                    onChange={(e) => setThreadUrl(e.target.value)}
                    placeholder="https://twitter.com/username/status/1234567890123456789"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && fetchThreadData()}
                  />
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Example
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['txt', 'md', 'pdf'].map((format) => (
                    <button
                      key={format}
                      onClick={() => setExportFormat(format as any)}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        exportFormat === format
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <FileText className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-sm font-medium">{format.toUpperCase()}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={fetchThreadData}
                disabled={isLoading || !threadUrl.trim()}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>Unrolling Thread...</>
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    Unroll Thread
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Thread Data */}
          {threadData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6 mb-8"
            >
              {/* Thread Header */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{threadData.threadTitle}</h3>
                    <p className="text-gray-600">by {threadData.author} ({threadData.authorHandle})</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(threadData.createdAt)} • {threadData.totalTweets} tweets
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={exportThread}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export {exportFormat.toUpperCase()}
                    </button>
                    <button
                      onClick={() => window.open(threadUrl, '_blank')}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Original
                    </button>
                  </div>
                </div>
              </div>

              {/* Thread Tweets */}
              <div className="space-y-4">
                {threadData.tweets.map((tweet) => (
                  <div key={tweet.id} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">{tweet.order}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-900">{tweet.author}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-500">{formatDate(tweet.timestamp)}</span>
                        </div>
                        
                        <p className="text-gray-800 mb-3 leading-relaxed">{tweet.text}</p>
                        
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {tweet.replies}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                            </svg>
                            {tweet.retweets}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                            </svg>
                            {tweet.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">How to Use</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Copy the URL of the first tweet in the thread</li>
                <li>• Paste it into the input field above</li>
                <li>• Choose your preferred export format</li>
                <li>• Click "Unroll Thread" to process</li>
                <li>• Export the thread for offline reading</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Features</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Unroll complete Twitter/X threads</li>
                <li>• Export as TXT, Markdown, or PDF</li>
                <li>• Preserve tweet order and metadata</li>
                <li>• Show engagement statistics</li>
                <li>• Clean, readable formatting</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Export Formats</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>TXT:</strong> Plain text for simple reading</li>
                <li>• <strong>Markdown:</strong> Formatted text with structure</li>
                <li>• <strong>PDF:</strong> Professional document format</li>
                <li>• Includes author info and timestamps</li>
                <li>• Preserves tweet numbering and order</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Use Cases</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• Save educational threads for reference</li>
                <li>• Archive important information</li>
                <li>• Share threads in presentations</li>
                <li>• Create readable documentation</li>
                <li>• Offline reading and study</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TwitterThreadUnroller;
